import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { build, buildParams, pool } from '../../../../../common/classes/request-builder';
import { ReactiveFormChangedField, ReactiveFormValue } from '@fitogest/reactive-form-builder/interfaces';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { getDropdownSelectedSingle } from '@fitogest/reactive-form-builder/utils';
import { BaseForm } from '../../../../base-form';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { SiexApiService } from '../../../../../service/api/siex-api.service';
import { TareaPlantacionModel } from '../../../../../models/tareas/tarea-plantacion.model';
import { MantenimientoModel } from '../../../../../models/trabajadores/mantenimiento.model';
import { TareasPlantacionEnvironmentDefinition } from '../environment-variables';
import { TareasPlantacionFormDefinition } from './form-control/form-definition';

@Component({
    selector: 'app-tareas-plantacion-form',
    standalone: false,
    templateUrl: './tareas-plantacion-form.component.html',
    styleUrls: ['./tareas-plantacion-form.component.scss'],
})
export class TareasPlantacionFormComponent extends BaseForm<TareaPlantacionModel> implements OnInit {
    public env: TareasPlantacionEnvironmentDefinition = new TareasPlantacionEnvironmentDefinition();
    public form!: TareasPlantacionFormDefinition;
    public formReady = false;
    public loadingData = true;
    private lastReloadedClientDropdownId = '';
    private isReloadingClientDropdowns = false;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public fincasApi: FincasApiService,
        public clientesApi: ClientesApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public siexApi: SiexApiService,
        public tareasApi: TareasApiService,
        public override router: Router,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.plantacion.PUT,
            tareasApi.plantacion.POST,
            'plantacion',
            'Actualizar Tarea Plantación',
            'Realizar Tarea Plantación',
            'Duplicar Tarea Plantación',
        );
    }

    public ngOnInit(): void {
        this.dashboard.clientChanges$.subscribe(() => {
            this.fincasApi.fincasGET.unsuscribe();
        });

        this.softInit(this.getType());
        void this.setupForm();
    }


    public formChanges(
        formModel: ReactiveFormValue<TareaPlantacionModel>,
        lastChangedFieldName: ReactiveFormChangedField<TareaPlantacionModel>,
    ): void {
        const changedFieldName = String(lastChangedFieldName);

        this.form.model = {
            ...this.form.model,
            ...formModel,
        };

        if (changedFieldName === 'hora_inicio' || changedFieldName === 'hora_fin') {
            this.updateHoras();
        }

        if (changedFieldName === 'mantenimiento') {
            this.form.updateMantenimientoLabel();
            this.dropdownLoadTrigger.next();
        }

        if (changedFieldName === 'id_cliente') {
            this.onClientChanged();
        }
    }

    public onAfterSend(): void {
        StorageManager.saveSessionDataDonJardin(
            this.form.model.id_finca ?? '',
            this.form.model.hora_inicio ?? '',
            this.form.model.hora_fin?.toString() ?? '',
        );
    }

    private async setupForm(): Promise<void> {
        this.model = this.getInitialModel() as TareaPlantacionModel;
        this.form = new TareasPlantacionFormDefinition(this.getType(), this.model);

        await this.initFormData();

        this.formReady = true;
    }

    private getInitialModel(): Partial<TareaPlantacionModel> {
        return {
            ...(this.getModel() ?? {}),
        };
    }

    private async initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        const buildsRequest = [
            build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true),
            build(this.clientesApi.clientesGET, this.form.lists.clientes, { or: ['razon_social', 'nombre'] }),
            build(this.trabajadoresApi.trabajadoresGET, this.form.lists.trabajadores,
                (it: MantenimientoModel) => `${it.nombre} ${it.apellidos || ''}`,
                true,
                true),
            build(this.siexApi.cultivosGET, this.form.lists.cultivos, 'cultivo', false),
            build(this.siexApi.variedadesGET, this.form.lists.variedades, 'variedad', true),
        ];

        return pool(buildsRequest, () => this.dropdownLoadTrigger.next())
            .then(() => {
                this.applyCreationDefaults();
                this.loadingData = false;
                this.finalizeExternalDropdowns();
                this.dropdownLoadTrigger.next();

                return true;
            })
            .catch((error: unknown) => {
                this.loadingData = false;
                this.finalizeExternalDropdowns();
                this.dropdownLoadTrigger.next();
                console.error('Error loading tareas plantación form data.', error);

                return true;
            });
    }

    private applyCreationDefaults(): void {
        const defaultFincaId = this.form.lists.fincas.values[0]?.value?.id ?? null;

        if (this.form.model.id_finca || !defaultFincaId) {
            return;
        }

        this.form.model = {
            ...this.form.model,
            id_finca: defaultFincaId,
        };
    }

    private updateHoras(): void {
        const horas = this.form.calculateHoras();

        if (this.form.model.horas === horas) {
            return;
        }

        this.form.model = {
            ...this.form.model,
            horas,
        };

        this.formBuilderRef?.updateForm({ horas }, true);
    }

    private onClientChanged(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente);

        this.form.model = {
            ...this.form.model,
            id_finca: null,
        };
        this.formBuilderRef?.updateForm({ id_finca: null }, false);

        if (!selectedClientId || this.isReloadingClientDropdowns || selectedClientId === this.lastReloadedClientDropdownId) {
            this.dropdownLoadTrigger.next();
            return;
        }

        this.reloadClientDropdowns(selectedClientId);
    }

    private reloadClientDropdowns(selectedClientId: string): void {
        this.isReloadingClientDropdowns = true;
        this.lastReloadedClientDropdownId = selectedClientId;
        this.resetExternalDropdowns();
        this.dropdownLoadTrigger.next();

        const selectedClient = getDropdownSelectedSingle(this.form.lists.clientes);

        if (selectedClient) {
            StorageManager.saveClient(selectedClient);
        }

        this.dashboard.emitClientChange();

        pool([
            buildParams(this.fincasApi.fincasGET, { id_cliente: selectedClientId }, this.form.lists.fincas, 'nombre', true),
        ], () => {
            this.form.lists.fincas.filtered = this.form.lists.fincas.values;
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.finalizeExternalDropdowns();
            this.isReloadingClientDropdowns = false;
            this.dropdownLoadTrigger.next();

            return;
        }).catch((error: unknown) => {
            this.finalizeExternalDropdowns();
            this.isReloadingClientDropdowns = false;
            this.dropdownLoadTrigger.next();
            console.error('Error reloading tareas plantación client dropdowns.', error);
        });
    }

    private getReactiveValueId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }
}
